import ConfirmAction from '@/components/confirm-action';
import ModeBadge from '@/components/console/mode-badge';
import StatusPill from '@/components/console/status-pill';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { formatDate } from '@/lib/format';
import { app_connection_path, app_connections_path, billing_path } from '@/rails/routes';
import type { ConnectionOption, ConnectionProvider } from '@/types/platform';
import { Link, router } from '@inertiajs/react';
import { useState } from 'react';

interface ConnectionCardProps {
  appId: string;
  option: ConnectionOption;
  canManage: boolean;
}

const DESCRIPTIONS: Record<ConnectionProvider, string> = {
  stripe:
    'Take card payments. You are the merchant; payments go to your Stripe account. CAP Rock Labs takes no cut.',
  square: 'Take payments with Square.',
  quickbooks: 'Send invoices and payments to your books.',
};

// The button that starts or restarts a connection, by where the connection stands.
const START_LABELS = {
  disconnected: 'Connect',
  pending: 'Continue setup',
  needs_reconnect: 'Reconnect',
} as const;

const ConnectionCard = ({ appId, option, canManage }: ConnectionCardProps) => {
  const [busy, setBusy] = useState(false);
  const connection = option.connection;
  const status = connection?.status ?? 'disconnected';
  const available = option.platformMode !== 'unconfigured';
  const canStart = status !== 'connected' && option.allowed && available;

  // The server answers with the provider's address, and Inertia follows it there.
  const start = () => {
    router.post(
      app_connections_path(appId),
      { provider: option.provider },
      { onStart: () => setBusy(true), onFinish: () => setBusy(false) }
    );
  };

  const disconnect = () => {
    router.delete(app_connection_path(appId, option.provider), {
      onStart: () => setBusy(true),
      onFinish: () => setBusy(false),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <h2 className="text-base">{option.label}</h2>
          {status === 'connected' ? <StatusPill tone="success">Connected</StatusPill> : null}
          {status === 'pending' ? <StatusPill tone="warning">Setup not finished</StatusPill> : null}
          {status === 'needs_reconnect' ? (
            <StatusPill tone="danger">Reconnect needed</StatusPill>
          ) : null}
          {status === 'disconnected' ? <StatusPill>Not connected</StatusPill> : null}
          {option.platformMode === 'demo' ? <ModeBadge mode="demo" /> : null}
        </CardTitle>
        <CardDescription>{DESCRIPTIONS[option.provider]}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-3 text-sm">
        {connection && status === 'connected' ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="text-muted-foreground">Mode</dt>
            <dd>{connection.mode === 'live' ? 'Live' : 'Test'}</dd>
            <dt className="text-muted-foreground">Account</dt>
            <dd>{connection.account ? `Ending in ${connection.account}` : '—'}</dd>
            <dt className="text-muted-foreground">Connected</dt>
            <dd>{formatDate(connection.connectedAt) || '—'}</dd>
          </dl>
        ) : null}

        {status === 'pending' ? (
          <p>Setup was started and not finished. Continue to finish it with {option.label}.</p>
        ) : null}

        {status === 'needs_reconnect' ? (
          <p className="break-words text-red-700">
            {connection?.lastError ?? `${option.label} stopped accepting this connection.`}
          </p>
        ) : null}

        {option.platformMode === 'demo' ? (
          <p className="text-muted-foreground">
            Demo: connects instantly to a stand-in account. Nothing real is linked.
          </p>
        ) : null}

        {available ? null : <p className="text-muted-foreground">Not available yet.</p>}

        {option.allowed ? null : (
          <p>
            {option.reason}{' '}
            <Link href={billing_path()} className="font-medium underline underline-offset-4">
              See plans
            </Link>
          </p>
        )}
      </CardContent>

      {canManage && !canStart && status !== 'connected' ? null : (
        <CardFooter className="flex-wrap gap-2">
          {canManage ? (
            <>
              {status !== 'connected' && canStart ? (
                <Button size="sm" onClick={start} disabled={busy}>
                  {START_LABELS[status]}
                </Button>
              ) : null}

              {status === 'connected' ? (
                <ConfirmAction
                  title={`Disconnect ${option.label}?`}
                  description={`This app stops using your ${option.label} account until you connect it again.`}
                  confirmLabel="Disconnect"
                  onConfirm={disconnect}
                >
                  <Button size="sm" variant="outline" disabled={busy}>
                    Disconnect
                  </Button>
                </ConfirmAction>
              ) : null}
            </>
          ) : (
            <p className="text-muted-foreground text-sm">An owner or admin connects accounts.</p>
          )}
        </CardFooter>
      )}
    </Card>
  );
};

export default ConnectionCard;
